import React, {useState, useEffect} from 'react'
import { useQuery, useLazyQuery, useMutation, gql } from "@apollo/client";

import ReturnsStepHeader from './components/returns_step_header';
import ReturnsConfirmationBlock from './components/returns_confirmation_block';
import ReturnsForm from './components/returns_form';
import ReturnsPreFormButtons from './components/returns_pre_form_buttons'
import ReturnsPaymentBlock from './components/returns_payment_block';
import ReturnsIntroduction from './components/returns_introduction';

const GET_CURRENT_USER = gql`
  query {
    me {
      firstname
      surname
      mobile
      email
      marketingOptIn
    }
  }
`;

const GET_RETAILER = gql`
  query getRetailer($slug: String!) {
    retailer(slug: $slug) {
      id
      name
      slug
      enablePrintInStore
      enablePrintAtHome
      hasLabelImage
      hasLogo
      hasNewReturnPage
      customYodelMobileTemplate
      hasShowPage
      address
      deductsChargeFromRefund
      returnDeductionAmount
      logo
      price
      printInStorePrice
      reasonCodes {
        id
        reason
      }
      reasonCodeLabel
      requireRetailerReference
      requireReturnReference
      retailerReferenceLabel
      retailerReferenceMaxLength
      retailerReferenceMinLength
      retailerUrl
      returnReferenceLabel
      returnReferenceMaxLength
      returnReferenceMinLength
      showPageTemplate
      useFreeFormReasonCode
      useReasonCodes
      useRetailerReference
      useReturnReference
      useCustomMobileTemplate
      landingIntroduction
      landingDimensionsAndPinsOverrideContent
      landingPreFormContent
      landingPostFormContent
      storesIntroduction
      storesDimensionsAndPinsOverrideContent
      storesPreFormContent
      storesPostFormContent
      lockersIntroduction
      lockersDimensionsAndPinsOverrideContent
      lockersPreFormContent
      lockersPostFormContent
      unlabelledInpostLockers
      labelledInpostLockers
    } 
  }
`;

const CREATE_RETURN = gql`
  mutation createReturnLabel($returnAttributes: ReturnAttributes!, $casualUserAttributes: CasualUserAttributes, $returnsSource: String) {
    createReturn(returnAttributes: $returnAttributes, casualUserAttributes: $casualUserAttributes, returnsSource: $returnsSource) {
      result {
        consignment
        guid
        paidAt
        price
        labelUrl
        paymentMethod
        printInStore
        state
        upiCode
        unlabelledInpostLocker
        isInpostLocker
      }
    }
  }
`;

export const CREATE_TRANSACTION = gql`
  mutation ($transactableGuid: ID, $transactableType: String) {
    createTransaction(transactableGuid: $transactableGuid, transactableType: $transactableType) {
      result
    }
  }
`;

export default function ReturnsMain({
  retailerInitialData,
  yodelReturn,
  csReturn,
  useInPostRetailerPageNewCopy,
  useInPostReturnsRemoveMarketingCheckbox
}) {

  const [isReady, setIsReady] = useState(false);
  const [retailer, setRetailer] = useState(retailerInitialData);
  const [currentUser, setCurrentUser] = useState(null);
  const [casualUserAttrs, setCasualUserAttrs] = useState(yodelReturn ? { email: yodelReturn.userEmail } : null);

  const [isStoreComponents, setIsStoreComponents] = useState(() => {
    if (yodelReturn) return !yodelReturn?.isInpostLocker;
    if (csReturn) return true;
    return retailerInitialData.enablePrintInStore || retailerInitialData.enablePrintAtHome;
  });

  const [isLockersComponents, setIsLockersComponents] = useState(() => {
    if (yodelReturn) return yodelReturn?.isInpostLocker;
    if (csReturn) return false;
    return retailerInitialData.unlabelledInpostLockers || retailerInitialData.labelledInpostLockers;
  });

  const showPageTemplate = retailerInitialData.showPageTemplate === "custom";

  const [showCreateNewLabel, setShowCreateNewLabel] = useState(yodelReturn ? false : !(isStoreComponents && isLockersComponents));
  const [showDownloadLabel, setShowDownloadLabel] = useState(yodelReturn && (yodelReturn.isLabelAvailable || yodelReturn.isInpostLocker));
  const [showPayments, setShowPayments] = useState(yodelReturn && !yodelReturn.isLabelAvailable && !yodelReturn.isInpostLocker);
  const [isPrintInStore, setIsPrintInStore] = useState(retailerInitialData.enablePrintInStore);
  const [isUnlabelledLocker, setIsUnlabelledLocker] = useState(yodelReturn ? yodelReturn.unlabelledInpostLocker : retailerInitialData.unlabelledInpostLockers);
  const [totalInPounds, setTotalInPounds] = useState(0);
  const [returnAttrs, setReturnAttrs] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [error, setError] = useState(null);
  const returnsSource = "web";

  const [retailerIntroduction, setRetailerIntroduction] = useState(retailer.retailerIntroduction);
  const [retailerDimensions, setRetailerDimensions] = useState(retailer.retailerDimensions);
  const [preFormContent, setPreFormContent] = useState(retailer.retailerPreFormContent);
  const [postFormContent, setPostFormContent] = useState(retailer.retailerPostFormContent);

  const [createReturn] = useMutation(CREATE_RETURN);
  const [createTransaction] = useMutation(CREATE_TRANSACTION);

  const [getCurrentUser] =
    useLazyQuery(GET_CURRENT_USER, {
      onCompleted: (data) => {
        if (data) {
          setCurrentUser(data.me); 
        }
        setIsReady(true);
      },
      onError: (error) => {
      }
    });

  const [getRetailer] =
    useLazyQuery(GET_RETAILER, {
      onCompleted: (data) => {
        setRetailer(data.retailer);
        getCurrentUser();
      },
      onError: (error) => {
      }
    });

  useEffect(() => {
    getRetailer({ variables: { slug: retailer.slug } });
  }, []);

  const updateStoreLocatorFilter = (inputValue) => {
    setTimeout(() => {
      const storeHiddenTag = document.querySelector("[data-store-locator-filter='paypoint']");
      const inpostHiddenTag = document.querySelector("[data-store-locator-filter='inpost']");
      const formContainer = document.querySelector("#store_locator_form");

      if (!inputValue) {
        storeHiddenTag?.remove();
        inpostHiddenTag?.remove();
      } else {
        const hiddenInput = document.createElement("input");
        hiddenInput.type = "hidden";
        hiddenInput.name = "search_filter";
        hiddenInput.id = "search_filter";
        hiddenInput.value = inputValue;
        hiddenInput.setAttribute("data-store-locator-filter", inputValue);
        hiddenInput.setAttribute("autocomplete", "off");
        formContainer?.appendChild(hiddenInput);
        inpostHiddenTag?.remove();
      }
    }, 100);
  };

  useEffect(() => {
    if (isStoreComponents && isLockersComponents) {
      updateStoreLocatorFilter();
    } else if (isStoreComponents && !isLockersComponents) {
      updateStoreLocatorFilter("paypoint");
    }else if (!isStoreComponents && isLockersComponents) {
      updateStoreLocatorFilter("in_post_locker");
    }
  }, [isStoreComponents, isLockersComponents]);

  useEffect(() => {
    countTotalInPounds();
  }, [retailer, isPrintInStore]);

  const createNewLabel = (returnAttributes, casualUserAttributes, totalPrice) => {
    setError(null);
    if (yodelReturn) {
      returnAttributes.guid = yodelReturn.guid;
    }
    createReturn(
      { variables: { returnAttributes, casualUserAttributes, returnsSource } }
    ).then(response => {
      if (totalPrice > 0 && isStoreComponents && !isLockersComponents && !csReturn) {
        createTransactionApi(response.data.createReturn.result);
      } else {
        const guid = response.data.createReturn.result.guid;
        if (csReturn) {
          window.location.href = `${window.location.origin}/cs/retailers/${retailer.slug}/returns/${guid}`;
        } else {
          window.location.href = `${window.location.origin}/returns/${retailer.slug}?guid=${guid}`;
        }
      }
    }).catch(error => {
      setError(error?.message || "There was an error, please try again.");
    });
  }

  const createTransactionApi = (retData) => {
    createTransaction(
      { variables: { transactableGuid: retData.guid, transactableType: "Return" } }
    ).then(response => {
      if (response?.data?.createTransaction?.result?.redirect_url) {
        window.location.href = response?.data?.createTransaction?.result?.redirect_url;
      } else {
        setError("There was an error, please try again.");
      }
    }).catch(error => {
      setError(error?.message || "There was an error, please try again.");
    });
  }
  

  const countTotalInPounds = () => {
    let total = retailer.price;

    if (isPrintInStore) {
      total += retailer.printInStorePrice;
    }

    setTotalInPounds(total);
  }
  
  const toggleCreateNewLabel = () => {
    setShowCreateNewLabel(!showCreateNewLabel);
  }

  const showPrePayment = (returnAttributes, casualUserAttributes, totalPrice) => {
    setReturnAttrs(returnAttributes);
    setCasualUserAttrs(casualUserAttributes);
    setTotalPrice(totalPrice);
    setShowPayments(true);
    setShowCreateNewLabel(false);
    setShowDownloadLabel(false);
  }

  const showForm = () => {
    setShowCreateNewLabel(true);
    setShowPayments(false);
    setShowDownloadLabel(false);
  }

  const headerTitle = () => {
    if (isStoreComponents && isLockersComponents) {
      return "Choose return method";
    } else if (isStoreComponents && !isLockersComponents) {
      if (useInPostRetailerPageNewCopy) {
        if (retailer.enablePrintAtHome && retailer.enablePrintInStore) {
          return "Return to an InPost Shop";
        } else {
          return "Return to any InPost Shop";
        }
      } else {
        return "Return to any Yodel Store";
      }
    } else if (!isStoreComponents && isLockersComponents) {
      return "Return to InPost Locker";
    } else {
      return "Create new label"
    }
  }

  const headerLabel = (showCreateNewLabel && !showDownloadLabel)
    ? headerTitle()
    : showDownloadLabel
      ? (isLockersComponents ?
        "Thank you for choosing to return your parcel to an InPost Locker"
        : (useInPostRetailerPageNewCopy
          ? "Thank you for choosing to return your parcel to an InPost Shop"
          : "Thank you for choosing to return your parcel to a Yodel Store")
      )
      : "Review & confirm";


  const isFirstStep = showCreateNewLabel;

  return (
    <>
      { !showPageTemplate &&
        <ReturnsIntroduction
            retailer={retailer}
            retailerIntroduction={retailerIntroduction}
            retailerDimensions={retailerDimensions}
            csReturn={csReturn}
          useInPostRetailerPageNewCopy={useInPostRetailerPageNewCopy}
        />
      }

      <div className="retailer__pre-form-content" dangerouslySetInnerHTML={{ __html: preFormContent }} />

      <div className="retailer__new-label-form-content">

        <div className="retailer__form-content">

          {!showCreateNewLabel && !showDownloadLabel && !showPayments &&
            <ReturnsPreFormButtons
              retailer={retailer}
              isStoreComponents={isStoreComponents}
              isLockersComponents={isLockersComponents}
              providesBoxLabel={retailerInitialData.providesBoxLabel}
              toggleCreateNewLabel={toggleCreateNewLabel}
              setIsStoreComponents={setIsStoreComponents}
              setIsLockersComponents={setIsLockersComponents}
              setRetailerIntroduction={setRetailerIntroduction}
              setRetailerDimensions={setRetailerDimensions}
              setPreFormContent={setPreFormContent}
              setPostFormContent={setPostFormContent}
              />
          }

          {(showCreateNewLabel || showDownloadLabel) &&
            <ReturnsStepHeader
              headerLabel={headerLabel}
              isFirstStep={isFirstStep}
              isStoreComponents={isStoreComponents}
              isLockersComponents={isLockersComponents}
            />
          }

          {!showPayments && !showDownloadLabel && isReady && //Shows the returns form
            <div className={`returns-form-container${showCreateNewLabel ? "" : " -hidden"}`}>
              <ReturnsForm
                isPrintInStore={isPrintInStore}
                isUnlabelledLocker={isUnlabelledLocker}
                useInPostRetailerPageNewCopy={useInPostRetailerPageNewCopy}
                useInPostReturnsRemoveMarketingCheckbox={useInPostReturnsRemoveMarketingCheckbox}
                retailer={retailer}
                currentUser={currentUser}
                returnAttributes={returnAttrs}
                casualUserAttributes={casualUserAttrs}
                csReturn={csReturn}
                totalInPounds={totalInPounds}
                error={error}
                createNewLabel={createNewLabel}
                setPrintInStore={setIsPrintInStore}
                setUnlabelledLocker={setIsUnlabelledLocker}
                showPrePayment={showPrePayment}
                isStoreComponents={isStoreComponents}
                isLockersComponents={isLockersComponents}
                setIsStoreComponents={setIsStoreComponents}
                setIsLockersComponents={setIsLockersComponents}
                setRetailerIntroduction={setRetailerIntroduction}
                setRetailerDimensions={setRetailerDimensions}
                setPreFormContent={setPreFormContent}
                setPostFormContent={setPostFormContent}
              />
            </div>
          }

          {showPayments &&
            <ReturnsPaymentBlock
              retailer={retailer}
              retailerAddress={retailerInitialData.address}
              returnAttributes={returnAttrs}
              casualUserAttributes={casualUserAttrs}
              currentUser={currentUser}
              totalPrice={totalPrice}
              isPrintInStore={isPrintInStore}
              isStoreComponents={isStoreComponents}
              isLockersComponents={isLockersComponents}
              error={error}
              onEditClick={showForm}
              paymentAction={createNewLabel}
            />
          }

          {showDownloadLabel && // Shows confirmation block when a label was created
            <ReturnsConfirmationBlock
              retailerAddress={retailerInitialData.address}
              returnGuid={yodelReturn.guid}
              isPrintInStore={yodelReturn.printInStore}
              isCsGenerated={yodelReturn.csGenerated}
              customerEmail={yodelReturn.userEmail}
              isUnlabelledLocker={isUnlabelledLocker}
              isStoreComponents={isStoreComponents}
              isLockersComponents={isLockersComponents}
              useInPostRetailerPageNewCopy={useInPostRetailerPageNewCopy}/>
          }
        </div>

        <div className="retailer__post-form_content" dangerouslySetInnerHTML={{ __html: postFormContent }} />
        
      </div>
    </>
  )
}
